import type { jsPDF } from "jspdf";
import { canv } from "@/canv/global";
import { sortGroupsByPosition } from "@/canvasController/helpers";
import { CommentWidget } from "@/elements/comment";
import { createImage, getLodByResolution, ImageWidget } from "@/elements/image";
import { Group } from "@/group/group";
import { decodeHtml } from "@/shared/lib/decodeHtml";
import { findFirstFrameWidget, isFrameInStructure } from "@/tools/cursorTool/utils";

export const getDocSize = (doc: jsPDF, visualWidth: number, visualHeight: number) => {
    const docWidth = doc.internal.pageSize.getWidth();
    const docHeight = doc.internal.pageSize.getHeight();

    const imageRatio = visualHeight / visualWidth;
    const docRatio = docHeight / docWidth;

    let width = 0;
    let height = 0;
    let x = 0;
    let y = 0;

    if (imageRatio > docRatio) {
        height = docHeight;
        width = height / imageRatio;
        x = (docWidth - width) / 2;
    } else {
        width = docWidth;
        height = width * imageRatio;
        y = (docHeight - height) / 2;
    }

    return { x, y, width, height };
};

export const queryAreaForExport = (area: Pick<Group, "startX" | "startY" | "width" | "height">) => {
    if (!canv.canvasController.hashGrid) return [];

    const areaCells = canv.canvasController.hashGrid.getCellsForElement(area);
    const found = new Set<Group>();

    const isIntersectingArea = (currentGroup: Group) => {
        const areaRight = area.startX + area.width;
        const areaBottom = area.startY + area.height;

        const groupRight = currentGroup.startX + currentGroup.width;
        const groupBottom = currentGroup.startY + currentGroup.height;

        return !(
            groupRight < area.startX ||
            currentGroup.startX > areaRight ||
            groupBottom < area.startY ||
            currentGroup.startY > areaBottom
        );
    };

    for (const cell of areaCells) {
        const cellGroups = canv.canvasController.hashGrid.grid.get(cell);
        if (!cellGroups) continue;

        for (const group of cellGroups) {
            if (!isIntersectingArea(group)) continue;

            const hasExclude = group.children.some((child) => child instanceof CommentWidget);

            if (!hasExclude) found.add(group);
        }
    }

    return Array.from(found);
};

export const mapGroupsWithoutFrameToFrames = (
    groupsWithFrames: Group[],
    groupsWithoutFrame: Group[],
): Record<string, Group[]> => {
    const result: Record<string, Group[]> = {};

    for (const frameGroup of groupsWithFrames) {
        const frameWidget = findFirstFrameWidget(frameGroup);
        if (!frameWidget) continue;

        const frameArea = frameWidget.getArea();
        const matchingGroups: Group[] = [];

        for (const otherGroup of groupsWithoutFrame) {
            if (otherGroup.isAnyItemInArea(frameArea)) {
                matchingGroups.push(otherGroup);
            }
        }

        if (!matchingGroups.length) {
            matchingGroups.push(...frameWidget.getOverlappedGroups());
        }

        if (matchingGroups.length) {
            result[frameGroup.UID] = matchingGroups;
        }
    }

    return result;
};

export async function prepareAllImagesForExport(groups: Group[]) {
    const preparePromises: Promise<Element>[] = [];

    for (const rootGroup of groups) {
        const stack: Group[] = [rootGroup];

        while (stack.length > 0) {
            const current = stack.pop();

            if (!current || !current.children) continue;

            for (const child of current.children) {
                if (child instanceof ImageWidget) {
                    const lod = getLodByResolution(child);
                    const bitmap = child.bitmapLODs.get(lod);
                    if (!bitmap && child.src) {
                        if (child.image) {
                            preparePromises.push(child.generateBitmap(child.image, lod));
                        } else {
                            const url = child.prepareImageUrl(false);
                            const image = await createImage(url, true);
                            child.image = image;
                            preparePromises.push(child.generateBitmap(image, lod));
                        }
                    }
                } else if (child instanceof Group) {
                    stack.push(child);
                }
            }
        }
    }

    await Promise.all(preparePromises);
}

export const savePDF = (doc: jsPDF) => {
    doc.save(`${decodeHtml(canv.getInitDataProp("boardName"))}.pdf`);
};

/**
 * Проверяет, нужно ли пропустить группу (например, если содержит комментарии)
 */
const shouldSkipGroup = (group: Group): boolean => {
    return group.children.some((c) => c instanceof CommentWidget);
};

/**
 * Рекурсивно обрабатывает фрейм и все его вложенные фреймы
 */
const processFrameGroupRecursive = (
    group: Group,
    groupsWithFrame: Group[],
    groupsWithoutFrame: Group[],
    processedUIDs: Set<string>,
): void => {
    // Добавляем текущий фрейм
    if (!processedUIDs.has(group.UID)) {
        groupsWithFrame.push(group);
        processedUIDs.add(group.UID);
    }

    const frameWidgetInstance = findFirstFrameWidget(group);
    if (!frameWidgetInstance) return;

    // Получаем все группы на этом фрейме
    const nestedGroups = frameWidgetInstance.getOverlappedGroups();

    // Разделяем на фреймы и не-фреймы
    const nestedFrames: Group[] = [];
    const nestedNonFrames: Group[] = [];

    for (const nestedGroup of nestedGroups) {
        if (shouldSkipGroup(nestedGroup) || processedUIDs.has(nestedGroup.UID)) {
            continue;
        }

        if (isFrameInStructure(nestedGroup)) {
            nestedFrames.push(nestedGroup);
        } else {
            nestedNonFrames.push(nestedGroup);
            processedUIDs.add(nestedGroup.UID);
        }
    }

    // Добавляем не-фреймы сразу
    groupsWithoutFrame.push(...nestedNonFrames);

    // Сортируем вложенные фреймы от левого верхнего к правому нижнему
    sortGroupsByPosition(nestedFrames);

    // Рекурсивно обрабатываем каждый вложенный фрейм
    for (const nestedFrame of nestedFrames) {
        processFrameGroupRecursive(nestedFrame, groupsWithFrame, groupsWithoutFrame, processedUIDs);
    }
};

/**
 * Собирает группы с фреймами и без фреймов из выбранных элементов,
 * включая вложенные группы на фреймах
 */
export const collectFrameGroups = (
    selectedGroups: Group[],
): { groupsWithFrame: Group[]; groupsWithoutFrame: Group[] } => {
    const groupsWithFrame: Group[] = [];
    const groupsWithoutFrame: Group[] = [];
    const processedUIDs = new Set<string>();

    // Собираем только верхнеуровневые фреймы
    const topLevelFrames: Group[] = [];
    for (const group of selectedGroups) {
        if (shouldSkipGroup(group)) continue;

        if (isFrameInStructure(group)) {
            topLevelFrames.push(group);
        } else {
            groupsWithoutFrame.push(group);
        }
    }

    // Сортируем верхнеуровневые фреймы от левого верхнего к правому нижнему
    sortGroupsByPosition(topLevelFrames);

    // Обрабатываем каждый фрейм рекурсивно (фрейм + его вложенные фреймы)
    for (const frame of topLevelFrames) {
        processFrameGroupRecursive(frame, groupsWithFrame, groupsWithoutFrame, processedUIDs);
    }

    return { groupsWithFrame, groupsWithoutFrame };
};
